import { FormEvent, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api, ApiError } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import type { UserBrief } from "@/messaging/types";
import { CheckIcon, CloseIcon, MegaphoneIcon, SearchIcon } from "@/messaging/chat/icons";

/** Broadcast-an-announcement modal — folded in from the removed standalone
 *  page. Portaled above the bell; Esc + backdrop close; scroll-locked. */
/** The backend's error codes, in the recipient's language. ``no_recipients``
 *  is now reachable in normal use: the sender is excluded from their own
 *  broadcast, so a one-person agency has nobody left to announce to. */
function errorText(err: unknown): string {
  if (!(err instanceof ApiError)) return "Couldn't send the announcement.";
  if (err.status === 403) return "You don't have permission to broadcast.";
  const detail = typeof err.message === "string" ? err.message : "";
  if (detail.includes("no_recipients")) {
    return "There's no one else in your agency to announce to.";
  }
  if (detail.includes("invalid_link_url")) {
    return "That link isn't valid — use a path like /home or a full https:// URL.";
  }
  if (detail.includes("title_required")) return "Give the announcement a title.";
  return detail || "Couldn't send the announcement.";
}

export function AnnouncementComposer({ onClose }: { onClose: () => void }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [audience, setAudience] = useState<"everyone" | "specific">("everyone");
  const [teammates, setTeammates] = useState<UserBrief[] | null>(null);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<number | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  useEffect(() => {
    if (audience === "specific" && teammates === null) {
      api<UserBrief[]>("/api/messaging/teammates")
        .then(setTeammates)
        .catch(() => setTeammates([]));
    }
  }, [audience, teammates]);

  const filtered = useMemo(() => {
    const list = teammates ?? [];
    const t = q.trim().toLowerCase();
    return t ? list.filter((u) => (u.full_name || u.email).toLowerCase().includes(t)) : list;
  }, [teammates, q]);

  const pickedIds = Object.keys(picked).filter((k) => picked[k]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || busy) return;
    if (audience === "specific" && pickedIds.length === 0) {
      setError("Pick at least one recipient.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        title: title.trim(),
        user_ids: audience === "specific" ? pickedIds : [],
      };
      if (body.trim()) payload.body = body.trim();
      if (link.trim()) payload.link_url = link.trim();
      const r = await api<{ sent_count: number }>("/api/notifications/announce", {
        method: "POST",
        json: payload,
      });
      setSent(r.sent_count);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1350] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl animate-[fadeIn_.15s_ease-out]"
      >
        <div className="card-header justify-between">
          <div className="flex items-center gap-2 text-slate-100">
            <MegaphoneIcon size={17} />
            <span className="text-sm font-semibold">New announcement</span>
          </div>
          <button onClick={onClose} className="icon-btn h-8 w-8" title="Close">
            <CloseIcon size={16} />
          </button>
        </div>

        {sent !== null ? (
          <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300">
              <CheckIcon size={24} />
            </span>
            <div className="text-sm font-medium text-slate-100">Announcement sent</div>
            <div className="text-xs text-slate-500">
              Delivered to {sent} {sent === 1 ? "person" : "people"}.
            </div>
            <button className="btn-primary !py-2 mt-1" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin px-5 py-4 space-y-4">
              <div>
                <label className="label">Title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={200}
                  required
                  autoFocus
                  placeholder="What's the headline?"
                  className="input w-full"
                />
              </div>
              <div>
                <label className="label">Message (optional)</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  maxLength={400}
                  rows={3}
                  placeholder="Add more detail…"
                  className="input w-full resize-y"
                />
              </div>
              <div>
                <label className="label">Link (optional)</label>
                <input
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  maxLength={400}
                  placeholder="/home or https://…"
                  className="input w-full"
                />
              </div>

              <div>
                <label className="label">Audience</label>
                <div className="inline-flex w-full rounded-lg border border-white/10 bg-white/[0.03] p-0.5 text-xs">
                  {(["everyone", "specific"] as const).map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setAudience(a)}
                      className={`flex-1 rounded px-3 py-1.5 font-medium transition ${
                        audience === a
                          ? "bg-accent-500/15 text-accent-200"
                          : "text-slate-400 hover:text-slate-200"
                      }`}
                    >
                      {a === "everyone" ? "Everyone" : "Pick people"}
                    </button>
                  ))}
                </div>
              </div>

              {audience === "specific" && (
                <div className="rounded-xl border border-white/5 bg-ink-950/40">
                  <div className="p-2">
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                        <SearchIcon size={15} />
                      </span>
                      <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search teammates"
                        className="input w-full !pl-9 !py-1.5 !text-xs"
                      />
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto scrollbar-thin">
                    {teammates === null && (
                      <div className="px-3 py-2 text-xs text-slate-500">Loading…</div>
                    )}
                    {teammates !== null && filtered.length === 0 && (
                      <div className="px-3 py-2 text-xs text-slate-500">No teammates match.</div>
                    )}
                    {filtered.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setPicked((p) => ({ ...p, [u.id]: !p[u.id] }))}
                        className={`flex w-full items-center gap-2.5 px-3 py-2 text-left transition ${
                          picked[u.id] ? "bg-accent-500/10" : "hover:bg-white/[0.03]"
                        }`}
                      >
                        <Avatar url={u.avatar_url} name={u.full_name} email={u.email} size="xs" />
                        <span className="min-w-0 flex-1 truncate text-[13px] text-slate-200">
                          {u.full_name || u.email}
                        </span>
                        <span
                          className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                            picked[u.id]
                              ? "border-accent-500 bg-accent-500 text-white"
                              : "border-white/20 text-transparent"
                          }`}
                        >
                          <CheckIcon size={11} />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {error && <div className="text-[12px] text-rose-400">{error}</div>}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-white/5 px-5 py-3">
              <button type="button" className="btn-secondary !py-2" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary !py-2"
                disabled={busy || !title.trim()}
              >
                {busy ? "Sending…" : "Broadcast"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}
